import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface SiteSettings {
  verificationRequired: boolean;
}

export default function AdminSettings() {
  const { toast } = useToast();

  const { data: settings, isLoading } = useQuery<SiteSettings>({
    queryKey: ["/api/admin/site-settings"],
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<{ verificationRequired: boolean }>) => {
      const res = await apiRequest("PATCH", "/api/admin/site-settings", updates);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/site-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/site-settings/verification-required"] });
      toast({
        title: "Settings updated",
        description: "Platform settings have been saved.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-wide" data-testid="text-settings-title">Platform Settings</h1>
        <p className="text-muted-foreground">Configure platform-wide settings</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Account Verification
          </CardTitle>
          <CardDescription>
            Control whether users must be verified before they can fully use the platform
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="verification-toggle" className="text-base font-medium">
                Require Account Verification
              </Label>
              <p className="text-sm text-muted-foreground">
                When enabled, users must be verified (by admin or via FMCSA) before they can post loads/trucks, send messages, submit quotes, or view contact details.
              </p>
            </div>
            <Switch
              id="verification-toggle"
              checked={settings?.verificationRequired ?? false}
              onCheckedChange={(checked) => updateMutation.mutate({ verificationRequired: checked })}
              disabled={updateMutation.isPending}
              data-testid="switch-verification-required"
            />
          </div>

          <div className="rounded-md border p-4">
            {settings?.verificationRequired ? (
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-sm">Verification is ON</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Unverified users can browse the marketplace but cannot view contact details, send messages, submit quotes, or post listings. They must be verified by an admin or self-verify through FMCSA.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-sm">Verification is OFF</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    All users have full platform access regardless of verification status. Use this temporarily when onboarding new users or during promotional periods.
                  </p>
                  <Badge variant="outline" className="mt-2 text-amber-600 border-amber-600">
                    Open Access
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
